import { describeError, useApi } from '@classgo/app-kit';
import { Button, Card, EmptyState, Modal } from '@classgo/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';

import { listQuery } from '../../academic/api';
import { AssignmentsForm } from '../../forms/assignments-form';
import { TeacherForm } from '../../forms/teacher-form';
import { useT } from '../../i18n';
import { recordQueries } from '../../records/api';
import { useTenant } from '../../tenant/tenant-provider';
import { InvitationStatus } from '../student-detail';

/**
 * One teacher (ticket #68): profile, invitation, and the assignments editor for the active year
 * (groups and subjects, homeroom).
 */
export const TeacherDetailScreen = () => {
  const t = useT();
  const api = useApi();
  const queries = useQueryClient();
  const { session, sede } = useTenant();
  const { teacherId = '' } = useParams({ strict: false });
  const teachers = useQuery(recordQueries.teachers(api));
  const groups = useQuery(listQuery(api, 'groups', { sedeId: sede.id }));
  const subjects = useQuery(listQuery(api, 'subjects'));
  const [modal, setModal] = useState<'edit' | 'assignments' | null>(null);
  const can = (key: string) => session.permissions.all || session.permissions.keys.includes(key);
  const teacher = (teachers.data ?? []).find((row) => row.id === teacherId);
  const refresh = () => queries.invalidateQueries({ queryKey: ['school', 'teachers'] });
  if (teachers.isPending) return <p className="text-sm text-gray-500">{t('common.loading')}</p>;
  if (teachers.isError || !teacher) {
    return (
      <EmptyState
        title={t('entity.error.title')}
        description={teachers.isError ? describeError(t, teachers.error) : t('teacher.notFound')}
        action={
          <Link
            to="/docentes"
            className="text-brand-600 underline underline-offset-2 hover:decoration-2"
          >
            {t('teacher.back')}
          </Link>
        }
      />
    );
  }
  const groupName = (id: string) =>
    (groups.data?.items ?? []).find((group) => group.id === id)?.name ?? id;
  const subjectName = (id: string | null) =>
    id
      ? ((subjects.data?.items ?? []).find((subject) => subject.id === id)?.name ?? id)
      : t('assignments.homeroom');
  const assignments = teacher.assignments ?? [];
  return (
    <div className="space-y-4">
      <Link
        to="/docentes"
        className="text-sm text-brand-600 underline underline-offset-2 hover:decoration-2"
      >
        ← {t('teacher.back')}
      </Link>
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">
              {teacher.givenName} {teacher.familyName}
            </h2>
            <p className="mt-1 text-sm text-gray-500">{teacher.email}</p>
            {can('users:invite') ? (
              <InvitationStatus userId={teacher.id} email={teacher.email} />
            ) : null}
          </div>
          {can('teachers:write') ? (
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setModal('edit')}>
                {t('teacher.edit')}
              </Button>
              <Button onClick={() => setModal('assignments')}>{t('assignments.edit')}</Button>
            </div>
          ) : null}
        </div>
      </Card>
      <Card title={t('assignments.title')}>
        {assignments.length === 0 ? (
          <p className="text-sm text-gray-500">{t('assignments.empty')}</p>
        ) : (
          <ul className="divide-y divide-gray-100 text-sm">
            {assignments.map((assignment) => (
              <li
                key={`${assignment.groupId}-${assignment.subjectId ?? 'homeroom'}`}
                className="flex items-center justify-between py-2"
              >
                <span className="font-medium text-gray-900">{groupName(assignment.groupId)}</span>
                <span className="text-gray-600">
                  {subjectName(assignment.subjectId)}
                  {assignment.isHomeroom ? ` · ${t('assignments.homeroom')}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Modal open={modal === 'edit'} onClose={() => setModal(null)} title={t('teacher.edit')}>
        {modal === 'edit' ? (
          <TeacherForm
            current={teacher}
            onDone={() => {
              setModal(null);
              void refresh();
            }}
          />
        ) : null}
      </Modal>
      <Modal
        open={modal === 'assignments'}
        onClose={() => setModal(null)}
        title={t('assignments.edit')}
        size="lg"
      >
        {modal === 'assignments' ? (
          <AssignmentsForm
            teacherId={teacher.id}
            initial={assignments}
            onDone={() => {
              setModal(null);
              void refresh();
            }}
          />
        ) : null}
      </Modal>
    </div>
  );
};
